import { sanitizeHtml, Text } from "@clipboard-health/ui-components";
import { type KnockGuideFilterParams } from "@knocklabs/client";
import { useGuides } from "@knocklabs/react";
import { CardContent, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { useHistory } from "react-router-dom";

import { Button } from "../../redesign/components/Button";
import { ShiftCardWrapper } from "../../redesign/Shift/Card/Wrapper";
import { useKnockMarkAsSeen } from "../hooks/useKnockMarkAsSeen";
import { handleGuideAction } from "../lib/handleGuideAction";
import { normalizeButtonContent } from "../lib/normalizeButtonContent";
import { type GuideButtonAction, type GuideButtonContent } from "../types";
import { KnockErrorBoundary } from "./KnockErrorBoundary";

interface KnockBannerContent {
  body: string;
  dismissible?: boolean;
  /* eslint-disable @typescript-eslint/naming-convention */
  primary_button?: GuideButtonContent;
  secondary_button?: GuideButtonContent;
  /* eslint-enable @typescript-eslint/naming-convention */
  title: string;
}

interface BannerContent {
  body: string;
  dismissible?: boolean;
  primaryButton?: GuideButtonContent;
  secondaryButton?: GuideButtonContent;
  title: string;
}

export type ShiftDiscoveryGuidesBannerFilter = KnockGuideFilterParams;

function normalizeContent(content?: KnockBannerContent): BannerContent | undefined {
  const title = content?.title?.trim();
  const body = content?.body?.trim();
  if (!title || !body) {
    return undefined;
  }

  return {
    title,
    body,
    primaryButton: normalizeButtonContent(content?.primary_button),
    secondaryButton: normalizeButtonContent(content?.secondary_button),
    dismissible: content?.dismissible ?? false,
  };
}

/**
 * Props for the ShiftDiscoveryGuidesBanner component.
 */
export interface ShiftDiscoveryGuidesBannerProps {
  filter: ShiftDiscoveryGuidesBannerFilter;
  primary?: GuideButtonAction;
  secondary?: GuideButtonAction;
}

interface BannerItemProps {
  content: BannerContent;
  key: string;
  primary?: GuideButtonAction;
  secondary?: GuideButtonAction;
  step: ReturnType<ReturnType<typeof useGuides>["guides"][number]["getStep"]>;
}

function BannerItem(props: BannerItemProps) {
  const { content, key, primary, secondary, step } = props;

  const history = useHistory();
  useKnockMarkAsSeen(step);

  if (!step) {
    return null;
  }

  const { title, body, primaryButton, secondaryButton, dismissible } = content;
  const actionData = { key, navigate: history.push, step };

  const handleClose = () => {
    step.markAsArchived();
  };

  return (
    <ShiftCardWrapper
      sx={(theme) => ({
        padding: theme.spacing(5),
        position: "relative",
      })}
    >
      <CardContent>
        <Stack spacing={3}>
          {dismissible && (
            <IconButton
              aria-label="Dismiss banner"
              iconType="close"
              size="medium"
              variant="ghost"
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
              }}
              onClick={handleClose}
            />
          )}

          <Text semibold variant="h5" sx={{ textAlign: "center", textWrap: "balance" }}>
            {title}
          </Text>

          <Text
            sx={{ textAlign: "center", paddingX: 7, textWrap: "balance" }}
            variant="body2"
            dangerouslySetInnerHTML={sanitizeHtml({ potentiallyDangerousHtml: body })}
          />

          {(primaryButton ?? secondaryButton) && (
            <Stack direction="row" spacing={3}>
              {secondaryButton && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={async () => {
                    await handleGuideAction({
                      ...actionData,
                      action: secondary,
                      content: secondaryButton,
                    });
                  }}
                >
                  {secondaryButton.text}
                </Button>
              )}
              {primaryButton && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={async () => {
                    await handleGuideAction({
                      ...actionData,
                      action: primary,
                      content: primaryButton,
                    });
                  }}
                >
                  {primaryButton.text}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </ShiftCardWrapper>
  );
}

function ShiftDiscoveryGuidesBannerInner(props: ShiftDiscoveryGuidesBannerProps) {
  const { filter, primary, secondary } = props;

  const { guides } = useGuides<KnockBannerContent>(filter);

  return (
    <>
      {guides.map((guide) => {
        const step = guide.getStep();
        const content = normalizeContent(step?.content);

        if (!step || !content) {
          return null;
        }

        return (
          <BannerItem
            key={guide.key}
            step={step}
            content={content}
            primary={primary}
            secondary={secondary}
          />
        );
      })}
    </>
  );
}

/**
 * A banner component that displays guide content from Knock for the Shift Discovery page.
 *
 * The component fetches and displays a guide banner based on the provided filter. It automatically
 * marks the guide as seen and handles user interactions such as dismissing the banner or clicking
 * action buttons.
 *
 * @example
 * ```tsx
 * <ShiftDiscoveryGuidesBanner
 *   filter={{ type: "shift-discovery-banner" }}
 *   primary={{
 *     onClick: (button) => console.log("Primary clicked:", button.action),
 *   }}
 *   secondary={{
 *     onClick: (button) => console.log("Secondary clicked:", button.action),
 *   }}
 * />
 * ```
 */
export function ShiftDiscoveryGuidesBanner(props: ShiftDiscoveryGuidesBannerProps) {
  return (
    <KnockErrorBoundary>
      <ShiftDiscoveryGuidesBannerInner {...props} />
    </KnockErrorBoundary>
  );
}
